import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	// Base styles
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none",
	{
		variants: {
			variant: {
				// Primary glass button (brand color)
				default: "glass-button glass-text",
				// Destructive glass button
				destructive:
					"bg-gradient-to-br from-red-500/20 to-red-600/10 backdrop-blur-sm border border-red-500/30 text-red-400 shadow-[0_4px_16px_rgba(239,68,68,0.2)] hover:from-red-500/30 hover:to-red-600/20 hover:border-red-500/50 hover:shadow-[0_6px_20px_rgba(239,68,68,0.3)] hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-[3px] focus-visible:ring-red-500/20",
				// Outline glass button
				outline:
					"glass-light glass-text hover:bg-[var(--glass-hover)] hover:border-[var(--glass-border-strong)] focus-visible:ring-[3px] focus-visible:ring-[var(--glass-ring)]",
				// Secondary glass button (subtle)
				secondary:
					"bg-[var(--glass-hover-subtle)] backdrop-blur-sm border border-[var(--glass-border)] glass-text-muted hover:bg-[var(--glass-hover)] hover:glass-text hover:border-[var(--glass-border-strong)] focus-visible:ring-[3px] focus-visible:ring-[var(--glass-ring-subtle)]",
				// Ghost button (no background)
				ghost:
					"glass-text-muted hover:bg-[var(--glass-hover)] hover:glass-text focus-visible:ring-[3px] focus-visible:ring-[var(--glass-ring-subtle)]",
				// Link style
				link: "glass-text-brand underline-offset-4 hover:underline",
				// Accent glass button (orange)
				accent:
					"bg-gradient-to-br from-[var(--brand-accent)]/20 to-[var(--brand-accent)]/10 backdrop-blur-sm border border-[var(--brand-accent)]/30 text-[var(--brand-accent)] shadow-[0_4px_16px_rgba(249,133,16,0.2)] hover:from-[var(--brand-accent)]/30 hover:to-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/50 hover:shadow-[0_6px_20px_rgba(249,133,16,0.3)] hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-[3px] focus-visible:ring-[var(--brand-accent)]/20",
			},
			size: {
				default: "h-10 px-5 py-2 has-[>svg]:px-4",
				sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
				lg: "h-12 rounded-lg px-8 has-[>svg]:px-6 text-base",
				icon: "size-10",
				"icon-sm": "size-8",
				"icon-lg": "size-12",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
